import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useDispatch } from 'react-redux'
import styled from '@emotion/styled'
import { useQueryClient } from '@tanstack/react-query'
import { DevicesResponse } from '@mobi/api-types'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Accordion } from '@mobi/component-library/Base/Accordion'
import { colors, font, spacing } from '@mobi/component-library/Foundations'
import { Spinner } from '@mobi/component-library/Legacy/Spinner'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { forgetDevice } from '@core/Areas/Login/helpers/api'
import { useAppSelector } from '@core/Store/hooks'
import { getTrustedDeviceDetails } from '@core/Areas/Login/Store/selectors'
import { type TrustedDeviceDetails, untrustDevice } from '@core/Areas/Login/Store'
import { mfaDevicesQueryKey, useMfaDevices } from '../Hooks/useMfaDevices'
import { usePasswordVerificationTray } from '../Hooks/usePasswordVerificationTray'
import { useTrustDevice } from '../Hooks/useTrustDevice'
import { trackMfaEvent } from '../Helpers/helpers'
import { PasswordVerificationTray } from './PasswordVerificationTray'
import { MFADeviceItem } from './MFADeviceItem'

const enum LocalConstants {
  AccordionStyleOverrideClass = 'accordion__mfa-device-group',
}

export const MAX_TRUSTED_DEVICES = 5

export const MFADeviceGroup: React.FC<{ accountNumber: number }> = ({ accountNumber }) => {
  const { trustDevice } = useTrustDevice()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const [isAccordionOpen, setIsAccordionOpen] = useState(
    searchParams.has('expand-mfa-devices') ?? false
  )
  const accountIdentifier = accountNumber.toString()
  const trustedDevices = useAppSelector(getTrustedDeviceDetails)
  const [pendingTrust, setPendingTrust] = useState(false)
  const currentDeviceDetails = trustedDevices[accountNumber]

  const { data, isLoading, error, refetch } = useMfaDevices(accountNumber, {
    enabled: isAccordionOpen,
    select: (data: DevicesResponse) => transformDeviceList(data, currentDeviceDetails),
  })

  const devices = sortBy(
    data?.devices || [],
    d => d.deviceLastAuthenticatedDate || '9999'
  ).toReversed()

  const currentDeviceKey = data?.currentDeviceKey

  const { isPasswordTrayOpen, closeVerifyPasswordTray, handleCognitoError, handleVerify } =
    usePasswordVerificationTray()

  useEffect(() => {
    if (error) {
      setIsAccordionOpen(false)
      handleCognitoError(error, async () => {
        await refetch()
        setIsAccordionOpen(true)
      })
    }
  }, [error, handleCognitoError, refetch])

  useEffect(() => {
    if (pendingTrust && trustedDevices[accountNumber]) {
      onTrustDevice()
      setPendingTrust(false)
    }
  }, [pendingTrust])

  async function handleToggle(wasExpanded: boolean) {
    setIsAccordionOpen(!wasExpanded)

    if (!wasExpanded) {
      // only trigger this event when the accordion is opened
      trackMfaEvent({
        interaction_type: 'Manage trusted devices',
        otpFlow: 'Settings',
      })
    }
  }

  const onTrustDevice = async (): Promise<void> => {
    if (!accountNumber) {
      return
    }
    try {
      await trustDevice(accountNumber)
    } catch (err) {
      setIsAccordionOpen(false)
      const isHandled = handleCognitoError(err, async () => {
        setPendingTrust(true)
        setIsAccordionOpen(true)
      })
      if (!isHandled) {
        addToast({
          id: 'trusted-device-error',
          isDismissable: true,
          timeoutMs: Infinity,
          title: 'Trusted Devices',
          subtitle: 'Adding your device was unsuccessful.',
          icon: 'error',
        })
      }
    }
  }

  const onForgetDevice = async (deviceKey: string) => {
    if (accountNumber) {
      try {
        await forgetDevice({ deviceKey })

        if (deviceKey === currentDeviceKey) {
          dispatch(
            untrustDevice({
              accountIdentifier,
            })
          )
        }

        // Invalidate and refetch the query
        await queryClient.invalidateQueries({ queryKey: mfaDevicesQueryKey(accountNumber) })

        addToast({
          id: 'forget-device-success',
          title: 'Trusted device removed',
          subtitle:
            'The selected device is no longer trusted. You will need to enter a secure code each time you login to your account with the device.',
          icon: 'success',
        })

        trackMfaEvent({ interaction_type: 'Remove trusted device', otpFlow: 'Settings' })
      } catch {
        addToast({
          id: 'forget-device-error',
          isDismissable: true,
          title: 'Unable to remove device',
          subtitle: 'Something went wrong. Please try again.',
          icon: 'error',
        })
      }
    }
  }

  const canTrustAdditionalDevices =
    devices.filter(d => d.deviceStatus === 'valid').length < MAX_TRUSTED_DEVICES

  return (
    <>
      {isLoading ? <Spinner /> : null}
      <DeviceGroupStyled>
        {accountNumber ? (
          <PasswordVerificationTray
            isOpen={isPasswordTrayOpen}
            onVerify={handleVerify}
            onCancel={closeVerifyPasswordTray}
            accountNumber={accountNumber}
          />
        ) : null}
        <Accordion
          ref={el => {
            el?.classList.add(LocalConstants.AccordionStyleOverrideClass)
          }}
          title={<DeviceGroupHeader />}
          onToggle={handleToggle}
          isExpandedControlled={isAccordionOpen}
        >
          {devices.length > 0 ? (
            devices.map(device => (
              <MFADeviceItem
                key={device.deviceKey}
                device={device}
                canTrust={canTrustAdditionalDevices}
                onDelete={onForgetDevice}
                onTrustDevice={onTrustDevice}
              />
            ))
          ) : error ? (
            <p>There was an error fetching your trusted devices</p>
          ) : (
            <NoDevicesStyled>
              <p>No trusted devices registered. Devices can be added during the login process.</p>
            </NoDevicesStyled>
          )}
        </Accordion>
      </DeviceGroupStyled>
    </>
  )
}

const DeviceGroupHeader: React.FC = () => {
  return (
    <DeviceGroupHeaderStyled>
      <Icon name='SolidCheckVerified02' color={colors.studio[500]} />
      <div>
        <h1>Manage trusted devices</h1>
        <p>
          Register your trusted devices for quicker, secure access. You can add up to{' '}
          {MAX_TRUSTED_DEVICES} devices.
        </p>
      </div>
    </DeviceGroupHeaderStyled>
  )
}

const DeviceGroupStyled = styled.div({
  marginTop: spacing.sm,

  // Override Accordion style
  [`&& .${LocalConstants.AccordionStyleOverrideClass}`]: {
    backgroundColor: colors.surface[100],
    borderRadius: '0.75rem',
    border: `0.5px solid ${colors.neutral[200]}`,
    boxShadow: 'none',
  },
})

const DeviceGroupHeaderStyled = styled.div({
  backgroundColor: colors.surface[100],
  display: 'flex',
  gap: spacing.smx1,
  alignItems: 'center',
  alignSelf: 'stretch',
  margin: '1rem 0',

  '> div': { margin: `0 ${spacing.smx1}` },
  h1: {
    fontFamily: font.family.primary,
    ...font.size.md,
    fontWeight: font.weight.semibold,
    margin: '0 0 0.25rem 0',
  },
  p: {
    color: colors.neutral[800],
    alignSelf: 'stretch',
    fontFamily: font.family.primary,
    fontWeight: font.weight.regular,
    ...font.size.base,
    margin: 0,
  },
})

const NoDevicesStyled = styled.div({
  padding: spacing.sm,
  textAlign: 'center',
  color: colors.neutral[600],

  '& p': {
    margin: 0,
    fontFamily: font.family.primary,
    ...font.size.sm,
  },
})

const transformDeviceList = (data: DevicesResponse, currentDeviceDetails: TrustedDeviceDetails) => {
  const trustedDevices = data.devices.filter(device => device.deviceStatus === 'valid')

  const hasCurrentDevice =
    currentDeviceDetails &&
    data.devices.some(device => device.deviceKey === currentDeviceDetails?.deviceKey)

  if (hasCurrentDevice) {
    const currentTrustedDeviceFromServer = trustedDevices.find(
      d => d.deviceKey === currentDeviceDetails.deviceKey
    )

    const deviceName = currentTrustedDeviceFromServer?.deviceName

    // if the current device is in the trusted list, then its name should have
    // 'this device' in it
    if (deviceName && !deviceName.toLowerCase().includes('this device')) {
      currentTrustedDeviceFromServer.deviceName = `${deviceName} (This Device)`
    }
  } else if (currentDeviceDetails) {
    trustedDevices.push({
      deviceName: 'This Device (Untrusted)',
      deviceKey: currentDeviceDetails?.deviceKey ?? '',
      deviceStatus: 'untrusted',
      deviceCreateDate: '',
      deviceLastAuthenticatedDate: '',
      deviceLastModifiedDate: '',
    })
  }

  return {
    devices: trustedDevices,
    currentDeviceKey: currentDeviceDetails?.deviceKey,
  }
}

function sortBy<T>(data: T[], fn: (v: T) => number | string): T[] {
  return data.sort((a, b) => {
    const a1 = fn(a)
    const b1 = fn(b)
    if (typeof a1 === 'number' && typeof b1 === 'number') {
      return a1 - b1
    } else {
      return a1.toString().localeCompare(b1.toString())
    }
  })
}
